// src/screens/PlatformFeeManagement.jsx
import { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";

import "../css/Dashboard.css";
import "../css/Notifications.css";
import useIdleLogout from "../hooks/useIdleLogout";

/* ---------------- CSV Export helpers ---------------- */
function csvEscape(cell) {
  const s = String(cell ?? "");
  const needsQuote = /[",\n]/.test(s);
  const esc = s.replace(/"/g, '""');
  return needsQuote ? `"${esc}"` : esc;
}
function downloadCSV(filename, rows) {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + rows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ---------------- ENV ---------------- */
const RULES_BASE = (process.env.REACT_APP_RULES_API_BASE || "").replace(/\/+$/, "");
const PLATFORM_FEE_RULES_API = RULES_BASE ? `${RULES_BASE}/platform-fee-rules` : "";
const RIDE_TYPES_API = (process.env.REACT_APP_RIDETYPES_API_GET_URL || "").replace(/\/+$/, "");

/* ---------------- network helpers ---------------- */
async function fetchJson(url, opts = {}, timeoutMs = 15000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...opts, signal: controller.signal });
    const text = await res.text();

    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }

    if (!res.ok) {
      const msg =
        json?.message ||
        json?.error ||
        json?.errors?.[0] ||
        `Request failed (HTTP ${res.status})`;
      throw new Error(msg);
    }

    return json ?? {};
  } finally {
    clearTimeout(t);
  }
}

function getAuthHeaders(withJson = false) {
  const t = (localStorage.getItem("access_token") || "").trim();
  const h = { Accept: "application/json" };
  if (withJson) h["Content-Type"] = "application/json";
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

/* ---------------- money/date helpers ---------------- */
const toMysqlDatetime = (dtLocal) => {
  if (!dtLocal) return null;
  const s = String(dtLocal);
  if (!s.includes("T")) return s;
  const [d, t] = s.split("T");
  const hhmm = (t || "").slice(0, 5);
  return `${d} ${hhmm}:00`;
};
const mysqlToDtLocal = (v) => {
  if (!v) return "";
  const s = String(v).replace("T", " ");
  const [d, t] = s.split(" ");
  const hhmm = (t || "").slice(0, 5);
  return d && hhmm ? `${d}T${hhmm}` : "";
};
const toLocalText = (s) => {
  if (!s) return "-";
  return String(s).replace("T", " ").replace(".000Z", "");
};

const nuToCents = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};
const centsToNu = (c) => {
  const n = Number(c);
  if (!Number.isFinite(n)) return 0;
  return (n / 100).toFixed(2);
};

const clampNum = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(min, Math.min(max, n));
};
const clampInt = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const x = Math.trunc(n);
  return Math.max(min, Math.min(max, x));
};

const percentToBp = (p) => {
  const n = Number(p);
  if (!Number.isFinite(n)) return 0;
  const c = Math.max(0, Math.min(100, n));
  return Math.round(c * 100);
};
const bpToPercent = (bp) => {
  const n = Number(bp);
  if (!Number.isFinite(n)) return "0";
  return (n / 100).toString();
};

/* ---------------- enums ---------------- */
const FEE_TYPES = ["percent", "fixed", "mixed"];
const APPLY_ON = ["subtotal", "fare_after_discounts", "driver_take_home_base"];
const TRIP_TYPES = ["instant", "scheduled", "pool"];
const CHANNELS = ["app", "web", "partner"];

/* ---------------- ride types normalize ---------------- */
const normalizeRideTypes = (payload) => {
  const arr = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.ride_types)
    ? payload.ride_types
    : [];

  const pick = (o, keys) => {
    for (const k of keys) {
      const v = o?.[k];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
    return "";
  };

  const items = arr
    .map((x) => {
      if (x == null) return null;
      if (typeof x === "string" || typeof x === "number") {
        const s = String(x).trim();
        return s ? { value: s, label: s } : null;
      }

      const value = pick(x, [
        "service_type",
        "ride_type",
        "ride_type_name",
        "name",
        "title",
        "type_name",
        "rideType",
        "rideTypeName",
      ]);

      const label = pick(x, [
        "display_name",
        "ride_type_name",
        "service_type",
        "ride_type",
        "name",
        "title",
      ]);

      const v = value || label;
      if (!v) return null;
      return { value: v, label: label || v };
    })
    .filter(Boolean);

  const seen = new Set();
  const dedup = [];
  for (const it of items) {
    const k = it.value.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    dedup.push(it);
  }

  dedup.sort((a, b) => a.label.localeCompare(b.label));
  return dedup;
};

function isFinanceUser() {
  try {
    if (localStorage.getItem("is_finance") === "true") return true;
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const raw = user?.role ?? user?.user_role ?? user?.role_name ?? user?.type ?? "";
    return String(raw).toLowerCase().replace(/\s+/g, " ").trim() === "finance";
  } catch { return false; }
}

/* ---------------- component ---------------- */
export default function PlatformFeeManagement() {
  useIdleLogout();
  const readOnly = isFinanceUser();

  /* ---------------- SERVICE TYPES (from ride types API) ---------------- */
  const [serviceOptions, setServiceOptions] = useState([]);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [serviceError, setServiceError] = useState("");

  const loadServiceTypes = useCallback(async () => {
    setServiceError("");
    if (!RIDE_TYPES_API) {
      setServiceOptions([]);
      setServiceError("Ride types API not configured. Set REACT_APP_RIDETYPES_API_GET_URL.");
      return;
    }
    try {
      setServiceLoading(true);
      const json = await fetchJson(RIDE_TYPES_API, { headers: getAuthHeaders(false) });
      const opts = normalizeRideTypes(json);
      setServiceOptions(opts);
    } catch (e) {
      setServiceOptions([]);
      setServiceError(e.message || "Failed to load service types.");
    } finally {
      setServiceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServiceTypes(); // auto fetch (NO reload button)
  }, [loadServiceTypes]);

  /* ---------------- CREATE FORM STATE ---------------- */
  const [countryCode, setCountryCode] = useState("BT");
  const [cityId, setCityId] = useState("THIMPHU");

  // service type now comes from dropdown
  const [serviceType, setServiceType] = useState("");

  const [tripType, setTripType] = useState("scheduled");
  const [channel, setChannel] = useState("app");

  const [feeType, setFeeType] = useState("percent");

  // shown to user as % / Nu
  const [feePercent, setFeePercent] = useState("20"); // 0..100
  const [feeFixedNu, setFeeFixedNu] = useState("0");
  const [minNu, setMinNu] = useState("40");
  const [maxNu, setMaxNu] = useState("400");

  const [applyOn, setApplyOn] = useState("subtotal");
  const [priority, setPriority] = useState("10"); // 1..10
  const [isActive, setIsActive] = useState(true);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  const [savingNew, setSavingNew] = useState(false);

  /* ---------------- LIST STATE ---------------- */
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  /* ---------------- INLINE EDIT STATE ---------------- */
  const [rowEditId, setRowEditId] = useState(null);
  const [rowDraft, setRowDraft] = useState(null);
  const [rowSaving, setRowSaving] = useState(false);

  const wideEdge = { marginInline: "clamp(-16px, -2.5vw, -32px)" };
  const TOP_PAD = { paddingInline: "clamp(16px, 2.5vw, 32px)" };

  const mapRule = (r) => ({
    id: r?.id ?? r?.platform_fee_rule_id ?? r?.rule_id ?? null,
    country_code: r?.country_code ?? null,
    city_id: r?.city_id ?? null,
    service_type: r?.service_type ?? null,
    trip_type: r?.trip_type ?? null,
    channel: r?.channel ?? null,

    fee_type: r?.fee_type ?? null,
    fee_percent_bp: Number(r?.fee_percent_bp ?? 0),
    fee_fixed_cents: Number(r?.fee_fixed_cents ?? 0),
    min_cents: Number(r?.min_cents ?? 0),
    max_cents: Number(r?.max_cents ?? 0),

    apply_on: r?.apply_on ?? null,
    priority: Number(r?.priority ?? 0),
    is_active: Number(r?.is_active ?? 0),

    starts_at: r?.starts_at ?? null,
    ends_at: r?.ends_at ?? null,
    created_at: r?.created_at ?? null,
  });

  /* ---------------- LOAD LIST ---------------- */
  const loadRules = useCallback(async () => {
    if (!PLATFORM_FEE_RULES_API) {
      setError("Platform fee rules API is not configured. Set REACT_APP_RULES_API_BASE.");
      setRules([]);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const url = new URL(PLATFORM_FEE_RULES_API, window.location.origin);
      url.searchParams.set("limit", "50");
      url.searchParams.set("offset", "0");

      const json = await fetchJson(url.toString(), { headers: getAuthHeaders(false) });
      const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      const mapped = arr.map(mapRule);
      setRules(mapped);

      if (rowEditId) {
        const stillThere = mapped.some((x) => x.id === rowEditId);
        if (!stillThere) {
          setRowEditId(null);
          setRowDraft(null);
        }
      }
    } catch (e) {
      setError(e.message || "Failed to load platform fee rules.");
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [rowEditId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  /* ---------------- INPUT CLAMP (CREATE) ---------------- */
  const handleFeePercentChange = (val) => {
    if (val === "") return setFeePercent("");
    const n = clampNum(val, 0, 100);
    if (n === null) return;
    setFeePercent(String(n));
  };
  const handleFeePercentBlur = () => {
    if (feePercent === "") return;
    const n = clampNum(feePercent, 0, 100);
    setFeePercent(String(n ?? 0));
  };

  const handlePriorityChange = (val) => {
    if (val === "") return setPriority("");
    const n = clampInt(val, 1, 10);
    if (n === null) return;
    setPriority(String(n));
  };
  const handlePriorityBlur = () => {
    if (priority === "") return;
    const n = clampInt(priority, 1, 10);
    setPriority(String(n ?? 10));
  };

  /* auto-pick first service if empty and options loaded */
  useEffect(() => {
    if (!serviceType && serviceOptions.length) {
      setServiceType(serviceOptions[0].value);
    }
  }, [serviceOptions, serviceType]);

  /* ---------------- CREATE ---------------- */
  const handleSubmitNew = async (e) => {
    e.preventDefault();

    if (!PLATFORM_FEE_RULES_API) {
      Swal.fire({ icon: "error", title: "Missing API", text: "Set REACT_APP_RULES_API_BASE" });
      return;
    }

    const cc = countryCode.trim();
    const cid = cityId.trim();
    const st = String(serviceType || "").trim();
    const pr = clampInt(priority, 1, 10);

    if (!cc) return Swal.fire({ icon: "error", title: "Country Code is required" });
    if (!startsAt) return Swal.fire({ icon: "error", title: "Starts At is required" });
    if (!st) return Swal.fire({ icon: "error", title: "Service Type is required" });
    if (!TRIP_TYPES.includes(tripType)) return Swal.fire({ icon: "error", title: "Trip Type is invalid" });
    if (!CHANNELS.includes(channel)) return Swal.fire({ icon: "error", title: "Channel is invalid" });
    if (!FEE_TYPES.includes(feeType)) return Swal.fire({ icon: "error", title: "Fee Type is invalid" });
    if (!APPLY_ON.includes(applyOn)) return Swal.fire({ icon: "error", title: "Apply On is invalid" });
    if (pr == null) return Swal.fire({ icon: "error", title: "Priority must be between 1 and 10" });

    if (feeType === "percent" || feeType === "mixed") {
      const p = clampNum(feePercent, 0, 100);
      if (p == null) return Swal.fire({ icon: "error", title: "Percent must be 0 to 100" });
    }

    const fee_percent_bp = feeType === "percent" || feeType === "mixed" ? percentToBp(feePercent) : 0;
    const fee_fixed_cents = feeType === "fixed" || feeType === "mixed" ? nuToCents(feeFixedNu) : 0;

    const payload = {
      country_code: cc,
      city_id: cid ? cid : null,
      service_type: st,
      trip_type: tripType,
      channel,

      fee_type: feeType,
      fee_percent_bp,
      fee_fixed_cents,
      min_cents: nuToCents(minNu),
      max_cents: nuToCents(maxNu),

      apply_on: applyOn,
      priority: pr,
      is_active: isActive ? 1 : 0,
      starts_at: toMysqlDatetime(startsAt),
      ends_at: endsAt ? toMysqlDatetime(endsAt) : null,
    };

    try {
      setSavingNew(true);

      await fetchJson(PLATFORM_FEE_RULES_API, {
        method: "POST",
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      Swal.fire({ icon: "success", title: "Platform fee rule created", timer: 1100, showConfirmButton: false });

      setStartsAt("");
      setEndsAt("");
      await loadRules();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Save failed", text: err.message || "Failed to save platform fee rule." });
    } finally {
      setSavingNew(false);
    }
  };

  /* ---------------- INLINE EDIT ---------------- */
  const beginRowEdit = (r) => {
    setRowEditId(r.id);

    const currentService = String(r.service_type ?? "").trim();
    const exists = serviceOptions.some((o) => o.value === currentService);

    setRowDraft({
      country_code: r.country_code ?? "",
      city_id: r.city_id ?? "",
      service_type: currentService || (serviceOptions[0]?.value ?? ""),

      // if API has a value not in dropdown, we still keep it (shown via injected option below)
      __service_missing: currentService && !exists ? currentService : "",

      trip_type: r.trip_type ?? "scheduled",
      channel: r.channel ?? "app",

      fee_type: r.fee_type ?? "percent",
      fee_percent: bpToPercent(r.fee_percent_bp ?? 0),
      fee_fixed_nu: centsToNu(r.fee_fixed_cents ?? 0),
      min_nu: centsToNu(r.min_cents ?? 0),
      max_nu: centsToNu(r.max_cents ?? 0),

      apply_on: r.apply_on ?? "subtotal",
      priority: String(r.priority ?? 10),
      is_active: Number(r.is_active) === 1 ? 1 : 0,
      starts_at: mysqlToDtLocal(r.starts_at),
      ends_at: mysqlToDtLocal(r.ends_at),
    });
  };

  const cancelRowEdit = () => {
    setRowEditId(null);
    setRowDraft(null);
  };

  const updateDraft = (k, v) => setRowDraft((prev) => ({ ...(prev || {}), [k]: v }));

  const updateDraftFeePercent = (val) => {
    if (val === "") return updateDraft("fee_percent", "");
    const n = clampNum(val, 0, 100);
    if (n === null) return;
    updateDraft("fee_percent", String(n));
  };
  const blurDraftFeePercent = () => {
    const cur = rowDraft?.fee_percent ?? "";
    if (cur === "") return;
    const n = clampNum(cur, 0, 100);
    updateDraft("fee_percent", String(n ?? 0));
  };

  const updateDraftPriority = (val) => {
    if (val === "") return updateDraft("priority", "");
    const n = clampInt(val, 1, 10);
    if (n === null) return;
    updateDraft("priority", String(n));
  };
  const blurDraftPriority = () => {
    const cur = rowDraft?.priority ?? "";
    if (cur === "") return;
    const n = clampInt(cur, 1, 10);
    updateDraft("priority", String(n ?? 10));
  };

  const saveRowEdit = async () => {
    if (!rowEditId || !rowDraft) return;
    if (!PLATFORM_FEE_RULES_API) return;

    const cc = String(rowDraft.country_code || "").trim();
    const cid = String(rowDraft.city_id || "").trim();
    const st = String(rowDraft.service_type || "").trim();
    const pr = clampInt(rowDraft.priority, 1, 10);

    if (!cc) return Swal.fire({ icon: "error", title: "Country Code is required" });
    if (!rowDraft.starts_at) return Swal.fire({ icon: "error", title: "Starts At is required" });
    if (!st) return Swal.fire({ icon: "error", title: "Service Type is required" });
    if (!TRIP_TYPES.includes(rowDraft.trip_type)) return Swal.fire({ icon: "error", title: "Trip Type is invalid" });
    if (!CHANNELS.includes(rowDraft.channel)) return Swal.fire({ icon: "error", title: "Channel is invalid" });
    if (!FEE_TYPES.includes(rowDraft.fee_type)) return Swal.fire({ icon: "error", title: "Fee Type is invalid" });
    if (!APPLY_ON.includes(rowDraft.apply_on)) return Swal.fire({ icon: "error", title: "Apply On is invalid" });
    if (pr == null) return Swal.fire({ icon: "error", title: "Priority must be between 1 and 10" });

    if (rowDraft.fee_type === "percent" || rowDraft.fee_type === "mixed") {
      const p = clampNum(rowDraft.fee_percent, 0, 100);
      if (p == null) return Swal.fire({ icon: "error", title: "Percent must be 0 to 100" });
    }

    const fee_percent_bp =
      rowDraft.fee_type === "percent" || rowDraft.fee_type === "mixed"
        ? percentToBp(rowDraft.fee_percent)
        : 0;

    const fee_fixed_cents =
      rowDraft.fee_type === "fixed" || rowDraft.fee_type === "mixed"
        ? nuToCents(rowDraft.fee_fixed_nu)
        : 0;

    const payload = {
      country_code: cc,
      city_id: cid ? cid : null,
      service_type: st,
      trip_type: rowDraft.trip_type,
      channel: rowDraft.channel,

      fee_type: rowDraft.fee_type,
      fee_percent_bp,
      fee_fixed_cents,
      min_cents: nuToCents(rowDraft.min_nu),
      max_cents: nuToCents(rowDraft.max_nu),

      apply_on: rowDraft.apply_on,
      priority: pr,
      is_active: Number(rowDraft.is_active) === 1 ? 1 : 0,
      starts_at: toMysqlDatetime(rowDraft.starts_at),
      ends_at: rowDraft.ends_at ? toMysqlDatetime(rowDraft.ends_at) : null,
    };

    try {
      setRowSaving(true);

      await fetchJson(`${PLATFORM_FEE_RULES_API}/${encodeURIComponent(rowEditId)}`, {
        method: "PUT",
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      Swal.fire({ icon: "success", title: "Changes saved", timer: 1000, showConfirmButton: false });

      cancelRowEdit();
      await loadRules();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Update failed", text: e.message || "Failed to update." });
    } finally {
      setRowSaving(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = async (id) => {
    if (!PLATFORM_FEE_RULES_API) return;

    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete this platform fee rule?",
      text: `Rule ID: ${id}`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!ok.isConfirmed) return;

    try {
      setDeletingId(id);

      await fetchJson(`${PLATFORM_FEE_RULES_API}/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: getAuthHeaders(false),
      });

      Swal.fire({ icon: "success", title: "Deleted", timer: 900, showConfirmButton: false });

      if (rowEditId === id) cancelRowEdit();
      await loadRules();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Delete failed", text: e.message || "Failed to delete." });
    } finally {
      setDeletingId(null);
    }
  };

  /* ---------------- EXPORT ---------------- */
  const exportAllToExcel = () => {
    const now = new Date();
    const ts = now.toISOString().replace(/[:.-]/g, "").slice(0, 15);
    const fname = `platform_fee_rules_${ts}.csv`;

    const headerLines = [
      ["Report Title", "Platform Fee Rules"],
      ["Exported At", now.toLocaleString()],
      [],
    ].map((row) => row.map(csvEscape).join(","));

    const tableHeader = [
      "SI.No",
      "ID",
      "Country",
      "City",
      "Service Type",
      "Trip Type",
      "Channel",
      "Fee Type",
      "Fee Percent (%)",
      "Fee Fixed (Nu)",
      "Min (Nu)",
      "Max (Nu)",
      "Apply On",
      "Priority",
      "Status",
      "Starts At",
      "Ends At",
    ]
      .map(csvEscape)
      .join(",");

    const rowsCsv = rules
      .map((r, idx) =>
        [
          String(idx + 1),
          r.id,
          r.country_code ?? "",
          r.city_id ?? "",
          r.service_type ?? "",
          r.trip_type ?? "",
          r.channel ?? "",
          r.fee_type ?? "",
          bpToPercent(r.fee_percent_bp ?? 0),
          centsToNu(r.fee_fixed_cents ?? 0),
          centsToNu(r.min_cents ?? 0),
          centsToNu(r.max_cents ?? 0),
          r.apply_on ?? "",
          r.priority ?? "",
          Number(r.is_active) === 1 ? "Active" : "Inactive",
          r.starts_at ?? "",
          r.ends_at ?? "",
        ].map(csvEscape).join(",")
      );

    downloadCSV(fname, [...headerLines, tableHeader, ...rowsCsv]);
  };

  /* ---------------- Export button hover ---------------- */
  const EXPORT_GREEN = "#1e9e56";
  const exportBtnBase = {
    border: `2px solid ${EXPORT_GREEN}`,
    color: EXPORT_GREEN,
    background: "transparent",
    borderRadius: 999,
    fontWeight: 600,
    transition: "all 0.25s ease",
    padding: "6px 18px",
    letterSpacing: "0.3px",
    boxShadow: "none",
  };
  const exportBtnHover = {
    background: EXPORT_GREEN,
    color: "#ffffff",
    boxShadow: "none",
    transform: "translateY(-1px)",
  };
  const exportBtnDisabled = {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "none",
    transform: "none",
  };
  const [isExportHover, setIsExportHover] = useState(false);
  const exportBtnStyle = {
    ...exportBtnBase,
    ...(isExportHover ? exportBtnHover : null),
    ...(!rules.length ? exportBtnDisabled : null),
  };

  const serviceSelectDisabled = serviceLoading || !serviceOptions.length;

  return (
    <div className="p-4 flex-grow-1">
      {/* ✅ scoped CSS: remove glow + make inline edit not congested */}
      <style>{`
        .pf-actions .btn,
        .pf-actions button {
          box-shadow: none !important;
          filter: none !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        .pf-actions { display: inline-flex; gap: 8px; align-items: center; flex-wrap: nowrap; white-space: nowrap; }
        .pf-actions .btn { border-radius: 12px !important; padding: 6px 12px !important; font-weight: 700 !important; min-width: 74px; }

        .pf-inline-input, .pf-inline-select {
          height: 34px;
          padding: 6px 10px;
          border-radius: 12px;
          min-width: 120px;
        }
        .pf-inline-mini { min-width: 90px; }
        .pf-inline-wide { min-width: 170px; }
        td.pf-edit-cell { vertical-align: middle; white-space: nowrap; }
      `}</style>

      <div className="card p-4 system-card">
        <h4 className="section-title">Platform Fee Management</h4>
        <p className="text-muted mb-4">
          Manage platform fee rules by country/city/service, trip type, channel, fee type and limits.
        </p>

        {/* ---------------- CREATE FORM ---------------- */}
        {!readOnly && <form onSubmit={handleSubmitNew} className="settings-form">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Country Code</label>
              <input
                type="text"
                className="form-control"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                placeholder="e.g., BT"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">City ID (optional)</label>
              <input
                type="text"
                className="form-control"
                value={cityId}
                onChange={(e) => setCityId(e.target.value)}
                placeholder="e.g., THIMPHU"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Service Type</label>
              <select
                className="form-select"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                disabled={serviceSelectDisabled}
              >
                <option value="">
                  {serviceLoading
                    ? "Loading services…"
                    : serviceOptions.length
                    ? "Select service"
                    : "No services found"}
                </option>
                {serviceOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {serviceError ? (
                <small className="text-danger">{serviceError}</small>
              ) : (
                <small className="text-muted">Fetched from Ride Types API</small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label">Trip Type</label>
              <select className="form-select" value={tripType} onChange={(e) => setTripType(e.target.value)}>
                {TRIP_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Channel</label>
              <select className="form-select" value={channel} onChange={(e) => setChannel(e.target.value)}>
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Fee Type</label>
              <select className="form-select" value={feeType} onChange={(e) => setFeeType(e.target.value)}>
                {FEE_TYPES.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Apply On</label>
              <select className="form-select" value={applyOn} onChange={(e) => setApplyOn(e.target.value)}>
                {APPLY_ON.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            {(feeType === "percent" || feeType === "mixed") && (
              <div className="col-md-3">
                <label className="form-label">Percent (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={feePercent}
                  onChange={(e) => handleFeePercentChange(e.target.value)}
                  onBlur={handleFeePercentBlur}
                  min="0"
                  max="100"
                  step="0.01"
                />
                <small className="text-muted">Saved as bp: {percentToBp(feePercent)}</small>
              </div>
            )}

            {(feeType === "fixed" || feeType === "mixed") && (
              <div className="col-md-3">
                <label className="form-label">Fixed (Nu)</label>
                <input
                  type="number"
                  className="form-control"
                  value={feeFixedNu}
                  onChange={(e) => setFeeFixedNu(e.target.value)}
                  min="0"
                  step="0.01"
                />
                <small className="text-muted">Saved as cents: {nuToCents(feeFixedNu)}</small>
              </div>
            )}

            <div className="col-md-3">
              <label className="form-label">Min (Nu)</label>
              <input type="number" className="form-control" value={minNu} onChange={(e) => setMinNu(e.target.value)} min="0" step="0.01" />
            </div>

            <div className="col-md-3">
              <label className="form-label">Max (Nu)</label>
              <input type="number" className="form-control" value={maxNu} onChange={(e) => setMaxNu(e.target.value)} min="0" step="0.01" />
            </div>

            <div className="col-md-3">
              <label className="form-label">Priority (lower wins)</label>
              <input
                type="number"
                className="form-control"
                value={priority}
                onChange={(e) => handlePriorityChange(e.target.value)}
                onBlur={handlePriorityBlur}
                min="1"
                max="10"
                step="1"
              />
              <small className="text-muted">Allowed range: 1 to 10</small>
            </div>

            <div className="col-md-3 d-flex align-items-center mt-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="pf-active"
                  className="form-check-input"
                  checked={isActive}
                  onChange={() => setIsActive((v) => !v)}
                />
                <label className="form-check-label" htmlFor="pf-active">Active rule</label>
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Starts At</label>
              <input type="datetime-local" className="form-control" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              <small className="text-muted">Required</small>
            </div>

            <div className="col-md-6">
              <label className="form-label">Ends At (optional)</label>
              <input type="datetime-local" className="form-control" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              <small className="text-muted">Leave empty for NULL</small>
            </div>
          </div>

          <button type="submit" className="btn btn-primary w-100 mt-4" disabled={savingNew}>
            {savingNew ? "Saving…" : "Save Platform Fee Rule"}
          </button>
        </form>}

        {/* ---------------- TABLE ---------------- */}
        <div className="mt-4" style={wideEdge}>
          <div className="d-flex align-items-center justify-content-between mb-2" style={TOP_PAD}>
            <h5 className="mb-0">Platform Fee Rules</h5>

            <button
              type="button"
              className="btn btn-outline-success btn-sm px-3 export-btn"
              onClick={exportAllToExcel}
              disabled={!rules.length}
              style={exportBtnStyle}
              onMouseEnter={() => setIsExportHover(true)}
              onMouseLeave={() => setIsExportHover(false)}
              onFocus={() => setIsExportHover(true)}
              onBlur={() => setIsExportHover(false)}
            >
              Export Excel
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mt-2 mb-3" style={TOP_PAD}>
              <small>{error}</small>
            </div>
          )}

          <div className="table-responsive contrast-surface" style={TOP_PAD}>
            <table className="table table-sm align-middle contrast-table w-100">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Sl.No</th>
                  <th style={{ width: 90 }}>ID</th>

                  <th style={{ minWidth: 110 }}>Country</th>
                  <th style={{ minWidth: 120 }}>City</th>
                  <th style={{ minWidth: 220 }}>Service Type</th>

                  <th style={{ minWidth: 120 }}>Trip Type</th>
                  <th style={{ minWidth: 110 }}>Channel</th>

                  <th style={{ minWidth: 120 }}>Fee Type</th>
                  <th style={{ minWidth: 140 }}>Percent (%)</th>
                  <th style={{ minWidth: 140 }}>Fixed (Nu)</th>

                  <th style={{ minWidth: 130 }}>Min (Nu)</th>
                  <th style={{ minWidth: 130 }}>Max (Nu)</th>

                  <th style={{ minWidth: 200 }}>Apply On</th>
                  <th style={{ minWidth: 110 }}>Priority</th>
                  <th style={{ minWidth: 120 }}>Status</th>

                  <th style={{ minWidth: 190 }}>Starts At</th>
                  <th style={{ minWidth: 190 }}>Ends At</th>

                  <th style={{ width: 200 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={18} className="text-muted text-center py-3">Loading…</td>
                  </tr>
                ) : rules.length === 0 ? (
                  <tr>
                    <td colSpan={18} className="text-muted text-center py-3">No platform fee rules found.</td>
                  </tr>
                ) : (
                  rules.map((r, idx) => {
                    const isRowEditing = rowEditId === r.id;
                    const d = isRowEditing ? (rowDraft || {}) : null;
                    const busyDelete = deletingId === r.id;

                    return (
                      <tr key={r.id ?? idx}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{r.id}</td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input className="form-control form-control-sm pf-inline-input pf-inline-mini" value={d.country_code ?? ""} onChange={(e) => updateDraft("country_code", e.target.value)} />
                          ) : (
                            r.country_code ?? "-"
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input className="form-control form-control-sm pf-inline-input pf-inline-mini" value={d.city_id ?? ""} onChange={(e) => updateDraft("city_id", e.target.value)} placeholder="optional" />
                          ) : (
                            r.city_id ?? "-"
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <select
                              className="form-select form-select-sm pf-inline-select pf-inline-wide"
                              value={d.service_type ?? ""}
                              onChange={(e) => updateDraft("service_type", e.target.value)}
                              disabled={serviceSelectDisabled}
                            >
                              {/* keep old unknown value if backend returned something not in ride types */}
                              {d.__service_missing ? (
                                <option value={d.__service_missing}>
                                  {d.__service_missing} (legacy)
                                </option>
                              ) : null}

                              <option value="">
                                {serviceLoading
                                  ? "Loading…"
                                  : serviceOptions.length
                                  ? "Select service"
                                  : "No services found"}
                              </option>

                              {serviceOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          ) : (
                            r.service_type ?? "-"
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm pf-inline-select pf-inline-mini" value={d.trip_type ?? "scheduled"} onChange={(e) => updateDraft("trip_type", e.target.value)}>
                              {TRIP_TYPES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          ) : (
                            r.trip_type ?? "-"
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm pf-inline-select pf-inline-mini" value={d.channel ?? "app"} onChange={(e) => updateDraft("channel", e.target.value)}>
                              {CHANNELS.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          ) : (
                            r.channel ?? "-"
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm pf-inline-select pf-inline-mini" value={d.fee_type ?? "percent"} onChange={(e) => updateDraft("fee_type", e.target.value)}>
                              {FEE_TYPES.map((f) => (
                                <option key={f} value={f}>{f}</option>
                              ))}
                            </select>
                          ) : (
                            r.fee_type ?? "-"
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm pf-inline-input pf-inline-mini"
                              value={d.fee_percent ?? "0"}
                              onChange={(e) => updateDraftFeePercent(e.target.value)}
                              onBlur={blurDraftFeePercent}
                              min="0"
                              max="100"
                              step="0.01"
                              disabled={d.fee_type === "fixed"}
                            />
                          ) : (
                            r.fee_type === "fixed" ? "—" : bpToPercent(r.fee_percent_bp ?? 0)
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm pf-inline-input pf-inline-mini"
                              value={d.fee_fixed_nu ?? "0.00"}
                              onChange={(e) => updateDraft("fee_fixed_nu", e.target.value)}
                              min="0"
                              step="0.01"
                              disabled={d.fee_type === "percent"}
                            />
                          ) : (
                            r.fee_type === "percent" ? "—" : centsToNu(r.fee_fixed_cents ?? 0)
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input type="number" className="form-control form-control-sm pf-inline-input pf-inline-mini" value={d.min_nu ?? "0.00"} onChange={(e) => updateDraft("min_nu", e.target.value)} min="0" step="0.01" />
                          ) : (
                            centsToNu(r.min_cents ?? 0)
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input type="number" className="form-control form-control-sm pf-inline-input pf-inline-mini" value={d.max_nu ?? "0.00"} onChange={(e) => updateDraft("max_nu", e.target.value)} min="0" step="0.01" />
                          ) : (
                            centsToNu(r.max_cents ?? 0)
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm pf-inline-select pf-inline-wide" value={d.apply_on ?? "subtotal"} onChange={(e) => updateDraft("apply_on", e.target.value)}>
                              {APPLY_ON.map((a) => (
                                <option key={a} value={a}>{a}</option>
                              ))}
                            </select>
                          ) : (
                            r.apply_on ?? "-"
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm pf-inline-input pf-inline-mini"
                              value={d.priority ?? "10"}
                              onChange={(e) => updateDraftPriority(e.target.value)}
                              onBlur={blurDraftPriority}
                              min="1"
                              max="10"
                              step="1"
                            />
                          ) : (
                            r.priority ?? "-"
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <select className="form-select form-select-sm pf-inline-select pf-inline-mini" value={Number(d.is_active) === 1 ? "1" : "0"} onChange={(e) => updateDraft("is_active", Number(e.target.value))}>
                              <option value="1">Active</option>
                              <option value="0">Inactive</option>
                            </select>
                          ) : Number(r.is_active) === 1 ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-secondary">Inactive</span>
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input type="datetime-local" className="form-control form-control-sm pf-inline-input pf-inline-wide" value={d.starts_at ?? ""} onChange={(e) => updateDraft("starts_at", e.target.value)} />
                          ) : (
                            toLocalText(r.starts_at)
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          {isRowEditing ? (
                            <input type="datetime-local" className="form-control form-control-sm pf-inline-input pf-inline-wide" value={d.ends_at ?? ""} onChange={(e) => updateDraft("ends_at", e.target.value)} />
                          ) : (
                            toLocalText(r.ends_at)
                          )}
                        </td>

                        <td className="pf-edit-cell">
                          <div className="pf-actions">
                            {!readOnly && (isRowEditing ? (
                              <>
                                <button type="button" className="btn btn-sm btn-outline-success" onClick={saveRowEdit} disabled={rowSaving}>
                                  {rowSaving ? "Saving..." : "Save"}
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelRowEdit} disabled={rowSaving}>
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => beginRowEdit(r)} disabled={rowEditId && rowEditId !== r.id}>
                                  Edit
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r.id)} disabled={busyDelete || (rowEditId && rowEditId !== r.id)}>
                                  {busyDelete ? "..." : "Delete"}
                                </button>
                              </>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer className="dashboard-footer mt-4">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
